import {
  FRAMEWORK_CONFIGS,
  type Framework,
  type FrameworkConfig,
  type Language,
} from '~/lib/stores/techStackSelection';
import type { DropdownOption } from '~/components/ui/Dropdown';
import type { ChatHistoryItem } from '~/lib/persistence';
import type { StoreFileAction } from '~/types/actions';
import * as Sentry from '@sentry/remix';

class FrameworkService {
  /**
   * Retrieves the full configuration object for a given framework.
   * @param framework The framework identifier.
   * @returns The configuration object for the specified framework.
   */
  getFrameworkConfig(framework: Framework): FrameworkConfig {
    return FRAMEWORK_CONFIGS[framework];
  }

  /**
   * Checks if a given framework is of type 'app'.
   * @param framework The framework identifier.
   * @returns `true` if the framework is an app framework, otherwise `false`.
   */
  isAppFramework(framework: Framework): boolean {
    return this.getFrameworkConfig(framework).type === 'app';
  }

  /**
   * Generates a list of dropdown options for all available frameworks.
   * This is used to populate UI elements like the framework selection dropdown.
   * @returns An array of `DropdownOption` objects.
   */
  getFrameworkOptions(): DropdownOption[] {
    return Object.entries(FRAMEWORK_CONFIGS).map(([value, config]) => ({
      value: value as Framework,
      label: config.label,
      icon: config.icon,
    }));
  }

  /**
   * Formats a framework identifier into a user-friendly display name.
   * @param framework The framework identifier.
   * @returns The formatted framework name.
   */
  formatFramework(framework?: Framework): string {
    if (!framework) {
      return '';
    }

    return FRAMEWORK_CONFIGS[framework]?.label || framework;
  }

  /**
   * Formats a language identifier into a user-friendly display name.
   * @param language The language identifier.
   * @returns The formatted language name.
   */
  formatLanguage(language?: Language): string {
    if (!language) {
      return '';
    }

    const languageMap: Record<Language, string> = {
      ts: 'TypeScript',
      js: 'JavaScript',
      // dart: 'Dart',  
    };
    return languageMap[language] || language;
  }

  /**
   * Extracts the framework and language from a chat history item's file actions.
   * @param messageData The chat history item.
   * @returns An object containing the detected framework and language.
   */
  extractFrameworks(messageData: ChatHistoryItem): {
    framework: string;
    language: string;
  } {
    const result = {
      framework: 'unknown',
      language: 'unknown',
    };

    const packageJson = messageData.fileActions.find(
      (action: StoreFileAction) => action.type === 'file' && action.filePath === 'package.json',
    );

    const hasMainDart = messageData.fileActions.find(
      (action: StoreFileAction) => action.type === 'file' && action.filePath === 'main.dart',
    );

    const hasHtmlFile = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.endsWith('.html'),
    );

    const hasTailwindConfig = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('tailwind.config'),
    );

    const hasViteConfig = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('vite.config.ts'),
    );

    const hasAngularJson = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('angular.json'),
    );

    const hasTsConfig = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('tsconfig.json'),
    );

    // Check for Flutter first since it won't have package.json
    if (hasMainDart) {
      result.framework = 'flutter';
      result.language = 'dart';
    } else if (hasTsConfig) {
      result.language = 'ts';
    } else {
      result.language = 'js';
    }

    try {
      if (hasAngularJson) {
        result.framework = 'angular_css';
      } else if (!hasViteConfig && hasHtmlFile) {
        result.framework = hasTailwindConfig ? 'html_tailwind' : 'html_css';
      }

      if (!packageJson || typeof packageJson.content !== 'string') {
        return result;
      }

      const packageData = JSON.parse(packageJson.content);

      // Check for React Native
      if (packageData.dependencies?.react && packageData.dependencies?.['react-native']) {
        result.framework = 'reactnative';
      }
      // Check for React and its UI libraries
      else if (packageData.dependencies?.react) {
        if (packageData.dependencies?.['@chakra-ui/react']) {
          result.framework = 'react_chakra';
        } else if (packageData.devDependencies?.tailwindcss) {
          const hasShadcnConfig = messageData.fileActions.some(
            (action: StoreFileAction) =>
              (action.type === 'file' && action.filePath === 'components.json') ||
              (typeof action.content === 'string' &&
                action.content.includes('@radix-ui') &&
                action.content.includes('clsx')),
          );
          result.framework = hasShadcnConfig ? 'react_shadcn' : 'react_tailwind';
        }
      }
      // Check for Vue
      else if (packageData.dependencies?.vue) {
        result.framework = packageData.devDependencies?.tailwindcss ? 'vue_tailwind' : 'vue_css';
      }

      return result;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error from extractFrameworks',
          packageJsonContent: packageJson?.content,
        },
      });
      return result;
    }
  }
}

export const frameworkService = new FrameworkService();
